// deps
import { Dispatch } from "react";

// internal-deps
import { ApiFetchState, isEmpty, PAYLATER_TYPES, VALID_PAYMENT_GROUP_TYPES } from "@swiggy-private/payment-core";
import { Err, None, Ok, Option, Result, Some } from "@swiggy-private/js-fp";
import {
    IPayLaterPaymentGroup,
    PayLaterLinkedData,
    PayLaterPaymentMethodData,
    PaymentGroupDataDef,
    PaymentMethod,
    PaymentMethodMeta,
} from "@swiggy-private/payment-ui";
import { PAYLATER_DISPLAY_NAMES } from "@swiggy-private/payment-core";
import { format } from "@swiggy-private/js-utils";
import { CheckoutActionTypes, getBridgeSdk } from "@swiggy-private/checkout-core";
import { LINK_ACCOUNT_SOURCE } from "@swiggy-private/native-bridge";
import { Tenant } from "@swiggy-private/native-bridge/dist/interfaces/Tenant";

// interfaces
import { GetPaymentMethodsResponse } from "@payment-client/interfaces/PaymentApi";

// helpers
import { getAllMethodsOfGroup, getPaymentMethodFromGroup, isPreferredGroup } from "./PaymentHelper";

// reducers
import {
    defaultPayLaterLinkStateValue,
    GetPayLaterLinkAction,
    GetPayLaterLinkActionTypes,
    PayLaterLinkState,
} from "@payment-client/reducers/PayLater";

// constants
import { options } from "@payment-client/constants/Options";
import { PAYMENT_FALLBACK_TYPES } from "@payment-client/constants/PaymentFallback";
import { LINK_PAYLATER } from "@payment-client/constants/PayLater";

// apis
import { checkPayLaterLinkStatus } from "@payment-client/apis/PayLater";
import { checkPayLaterLinkStatusPublic } from "@payment-client/apis/PublicPayLater";
import { isTurboPaymentGroup } from "./TurboPayment";

export interface CheckPayLaterLinkResponse {
    name: string;
    response: Result<PayLaterLinkedData, Error | string>;
}

const defaultPayLaterLinkedData: PayLaterLinkedData = {
    linked: false,
};

export function isPayLaterGroup(group: PaymentGroupDataDef): group is IPayLaterPaymentGroup["paymentData"] {
    return group.groupName.toUpperCase() === VALID_PAYMENT_GROUP_TYPES.PAY_LATER;
}

export function findPayLaterPaymentMethod(
    paymentGroups: GetPaymentMethodsResponse["paymentGroup"],
    payLaterCode: string,
): Option<PayLaterPaymentMethodData> {
    const payLaterMethod = Some(paymentGroups.find(isPayLaterGroup)).andThen((group) =>
        getPaymentMethodFromGroup(group, payLaterCode),
    );

    return payLaterMethod.orElse(() =>
        Some(paymentGroups.find((group) => isPreferredGroup(group) || isTurboPaymentGroup(group))).andThen((group) =>
            getPaymentMethodFromGroup(group, payLaterCode),
        ),
    );
}

export function isPayLaterMethod(method: PaymentMethod<PaymentMethodMeta>): method is PayLaterPaymentMethodData {
    return method.groupName.toUpperCase() === VALID_PAYMENT_GROUP_TYPES.PAY_LATER;
}

export function getPayLaterPaymentGroup(
    paymentGroups: GetPaymentMethodsResponse["paymentGroup"],
): Option<PaymentGroupDataDef> {
    // Case 1: check for Paylater payment group
    const payLaterGroup = Some(paymentGroups.find(isPayLaterGroup));
    return payLaterGroup.orElse(() => {
        // Case2: If Paylater group is not present,
        // then check if preferred payment group is present
        // AND LP payment method is in preferred group
        const preferredGroup = Some(paymentGroups.find(isPreferredGroup));
        if (preferredGroup.isNone()) return None;

        const group = preferredGroup.unwrap();
        const isPaylaterPreferred = group.paymentMethods.some(
            (method) => method.paymentCode === PAYMENT_FALLBACK_TYPES.LAZYPAY,
        );
        return isPaylaterPreferred ? preferredGroup : None;
    });
}

export function getAllPaylaterMethods(
    paymentGroups: GetPaymentMethodsResponse["paymentGroup"],
): PayLaterPaymentMethodData[] {
    return getAllMethodsOfGroup(paymentGroups, VALID_PAYMENT_GROUP_TYPES.PAY_LATER);
}

export function processPayLaterLinkResponse(response: PayLaterLinkedData, payLaterCode: string): PayLaterLinkedData {
    const payLaterData = response ?? defaultPayLaterLinkedData;

    if (!payLaterData.linked && payLaterCode === PAYLATER_TYPES.LAZYPAY) {
        payLaterData["meta"] = options.lazyPayMeta;
    }
    return payLaterData;
}

export async function fetchPaylaterLinkStatus(
    apiHost: string,
    paymentMethodMeta: {
        name: string;
        paymentCode: string;
        payableAmount?: string;
        meta?: PaymentMethodMeta;
    },
    checkoutStateType?: CheckoutActionTypes,
    isPublicApiEnable?: boolean,
): Promise<CheckPayLaterLinkResponse> {
    const { name, paymentCode, payableAmount, meta } = paymentMethodMeta;

    const checkPayLaterLinkStatusFn = isPublicApiEnable ? checkPayLaterLinkStatusPublic : checkPayLaterLinkStatus;

    try {
        // if paymentCode is PAYLATER_TYPES.SIMPL then based on  simplRedirectionUrl and a type PAYLATER_SIMPL_LINKING_SUCCESS_PROCEED_WITH_ORDER we can decide
        // if account is linked or not no need to to checkPayLaterLinkStatus api call
        if (paymentCode === PAYLATER_TYPES.SIMPL) {
            const simplRedirectionUrl = meta?.simplRedirectionUrl;
            const linked =
                isEmpty(simplRedirectionUrl) ||
                checkoutStateType === CheckoutActionTypes.PAYLATER_SIMPL_LINKING_SUCCESS_PROCEED_WITH_ORDER;
            return {
                name,
                response: Ok({
                    linked,
                }),
            };
        }
        // If payment code is amazon pay later, then link status is decided via lnk nb call
        // hence we don't need this. This state is never gtting read in the component
        else if (paymentCode === PAYLATER_TYPES.AMAZON_PAYLATER) {
            return {
                name,
                response: Ok({
                    linked: false,
                }),
            };
        } else {
            const response = await checkPayLaterLinkStatusFn(apiHost, name, payableAmount);

            return {
                name,
                response: Ok(processPayLaterLinkResponse(response, paymentCode)),
            };
        }
    } catch (err) {
        return { name, response: Err(err) };
    }
}

export function dispatchPayLaterLinkResponse(
    { name, response }: CheckPayLaterLinkResponse,
    dispatch: Dispatch<GetPayLaterLinkAction>,
): void {
    response.match({
        ok: (payload) => {
            dispatch({ name, type: GetPayLaterLinkActionTypes.SUCCESS, payload });
        },
        err: () => dispatch({ name, type: GetPayLaterLinkActionTypes.ERROR }),
    });
}

export function defaultReducerStateBuilder(paymentMethods: PayLaterPaymentMethodData[]): PayLaterLinkState {
    return paymentMethods.reduce<PayLaterLinkState>((acc, cur) => {
        acc[cur.name] = { ...defaultPayLaterLinkStateValue, fetchState: ApiFetchState.LOADING };
        return acc;
    }, {});
}

export const getLinkPayLaterPageTitle = (walletCode: PAYLATER_TYPES): string => {
    const walletDisplayName = PAYLATER_DISPLAY_NAMES[walletCode];
    return format(LINK_PAYLATER, walletDisplayName);
};

export const sendLinkSuccessStatusToNative = (paymentCode: string, tenant?: string) => {
    getBridgeSdk()
        .sendLinkAccountStatusToNative({
            paymentCode: paymentCode,
            status: "SUCCESS",
            source: LINK_ACCOUNT_SOURCE.PAYMENTS,
            ...(!isEmpty(tenant) && { tenant: tenant as Tenant }),
        })
        .catch((err) => {
            __DEV__ && console.error(err);
        });
};

export enum AmazonPayLaterField {
    ENABLED_STATUS = "ENABLED_STATUS",
    LINKED_STATUS = "LINKED_STATUS",
    CREDIT_LIMIT = "CREDIT_LIMIT",
    KEY_FACT_STATEMENT_URL = "KEY_FACT_STATEMENT_URL",
}

type AmazonPayLaterFieldReturnType = {
    [AmazonPayLaterField.ENABLED_STATUS]: boolean;
    [AmazonPayLaterField.LINKED_STATUS]: boolean | undefined;
    [AmazonPayLaterField.CREDIT_LIMIT]: string | undefined;
    [AmazonPayLaterField.KEY_FACT_STATEMENT_URL]: string | undefined;
};

const AMAZON_PAY_LATER_NONE_DEFAULTS: AmazonPayLaterFieldReturnType = {
    [AmazonPayLaterField.ENABLED_STATUS]: false,
    [AmazonPayLaterField.LINKED_STATUS]: false,
    [AmazonPayLaterField.CREDIT_LIMIT]: "",
    [AmazonPayLaterField.KEY_FACT_STATEMENT_URL]: "",
};

export function getAmazonPayLaterInfo<F extends AmazonPayLaterField>(
    paymentResponse: GetPaymentMethodsResponse,
    field: F,
): AmazonPayLaterFieldReturnType[F] {
    const method = findPayLaterPaymentMethod(paymentResponse.paymentGroup, PAYLATER_TYPES.AMAZON_PAYLATER);

    if (method.isNone()) {
        return AMAZON_PAY_LATER_NONE_DEFAULTS[field];
    }

    const unwrapped = method.unwrap();
    switch (field) {
        case AmazonPayLaterField.ENABLED_STATUS:
            return unwrapped.enabled as AmazonPayLaterFieldReturnType[F];
        case AmazonPayLaterField.LINKED_STATUS:
            return unwrapped.meta?.isApayLaterLinked as AmazonPayLaterFieldReturnType[F];
        case AmazonPayLaterField.CREDIT_LIMIT:
            return unwrapped.balance as AmazonPayLaterFieldReturnType[F];
        case AmazonPayLaterField.KEY_FACT_STATEMENT_URL:
            return unwrapped.meta?.keyFactStatementUrl as AmazonPayLaterFieldReturnType[F];
        default:
            return AMAZON_PAY_LATER_NONE_DEFAULTS[field];
    }
}
